/**
 * Profile Store - Zustand store for user profile state management.
 * 
 * This store manages:
 * - User tier and identity
 * - Intelligence layer toggles (pantry, macro, wine)
 * - Variety mode selection
 * - Meal and wine pairing settings
 * 
 * Features:
 * - Optimistic updates with rollback on failure
 * - Type-safe state and actions
 * - Testable without React components
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
    VarietyMode,
    UserTier,
    Toggles,
    MealSettings,
    WineSettings,
    ProfileApiResponse,
    isValidVarietyMode,
    VARIETY_MODES,
    DietPhilosophy,
    MacroProfile,
} from '../types';

// ============================================================================
// Store Types
// ============================================================================

interface ProfileState {
    // User identity
    userTier: UserTier;
    userName: string | null;

    // Intelligence toggles
    toggles: Toggles;

    // Settings
    varietyMode: VarietyMode;
    wineSettings: WineSettings;
    mealSettings: MealSettings;
    dietPhilosophy: DietPhilosophy | null;
    macroProfile: MacroProfile | null;
    householdSize: number; // V13: Serving sizes support

    // Subscription
    subscriptionStatus: string | null;
    trialEndsAt: string | null;

    // Loading states
    loading: boolean;
    varietyModeSaving: boolean;
    error: Error | null;
}

interface ProfileActions {
    // Loaders
    loadProfile: (getToken: () => Promise<string>, fetchProfile: (token: string) => Promise<ProfileApiResponse>) => Promise<void>;

    // Variety Mode
    setVarietyMode: (
        newMode: VarietyMode,
        getToken: () => Promise<string>,
        updateToggles: (token: string, payload: unknown) => Promise<any>,
        triggerToast?: (msg: string) => void
    ) => Promise<any>;

    // Toggle handlers
    setToggles: (toggles: Toggles) => void;
    setMealSettings: (settings: MealSettings) => void;
    setWineSettings: (settings: WineSettings) => void;

    // Diet & Macro
    setDietPhilosophy: (
        philosophy: DietPhilosophy | null,
        getToken: () => Promise<string>,
        updateToggles: (token: string, payload: unknown) => Promise<any>,
        triggerToast?: (msg: string) => void
    ) => Promise<any>;

    setMacroProfile: (
        profile: MacroProfile | null,
        getToken: () => Promise<string>,
        updateToggles: (token: string, payload: unknown) => Promise<any>,
        triggerToast?: (msg: string) => void
    ) => Promise<any>;

    setHouseholdSize: (
        size: number,
        getToken: () => Promise<string>,
        updateToggles: (token: string, payload: unknown) => Promise<any>,
        triggerToast?: (msg: string) => void
    ) => Promise<any>;

    // Reset
    reset: () => void;
}

export type ProfileStore = ProfileState & ProfileActions;

// ============================================================================
// Default State
// ============================================================================

const DEFAULT_TOGGLES: Toggles = { pantry: true, macro: true, wine: false };
const DEFAULT_MEAL_SETTINGS: MealSettings = { Breakfast: true, Lunch: true, Dinner: true, Snack: false };
const DEFAULT_WINE_SETTINGS: WineSettings = { Breakfast: false, Lunch: false, Dinner: true };

const initialState: ProfileState = {
    userTier: 'free',
    userName: null,
    toggles: DEFAULT_TOGGLES,
    varietyMode: 'balanced',
    wineSettings: DEFAULT_WINE_SETTINGS,
    mealSettings: DEFAULT_MEAL_SETTINGS,
    dietPhilosophy: null,
    macroProfile: null,
    householdSize: 1, // V13: Default to 1
    subscriptionStatus: 'inactive',
    trialEndsAt: null,
    loading: true,
    varietyModeSaving: false,
    error: null,
};

// ============================================================================
// Store Implementation
// ============================================================================

export const useProfileStore = create<ProfileStore>()(
    devtools(
        (set, get) => ({
            ...initialState,

            loadProfile: async (getToken, fetchProfile) => {
                set({ loading: true, error: null });

                try {
                    const token = await getToken();
                    const profile = await fetchProfile(token);

                    // Defensive: Validate tier
                    const validTiers: UserTier[] = ['free', 'pro', 'premium', 'unlimited'];
                    const tier = validTiers.includes(profile.tier as UserTier)
                        ? (profile.tier as UserTier)
                        : 'free';

                    // Defensive: Validate variety_mode
                    const varietyMode = isValidVarietyMode(profile.variety_mode)
                        ? profile.variety_mode
                        : 'balanced';

                    // Enforce wine toggle based on tier and trial status
                    const isTrialActive = profile.trial_ends_at ? new Date(profile.trial_ends_at) > new Date() : false;
                    const isSommelierEntitled = tier === 'premium' || tier === 'unlimited' || isTrialActive;

                    const toggles: Toggles = {
                        ...DEFAULT_TOGGLES,
                        ...profile.toggles,
                        wine: isSommelierEntitled ? (profile.toggles?.wine ?? false) : false,
                    };

                    set({
                        userTier: tier,
                        userName: profile.name || (profile.email ? profile.email.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Chef'),
                        toggles,
                        varietyMode,
                        wineSettings: { ...DEFAULT_WINE_SETTINGS, ...profile.wine_pairing_settings },
                        mealSettings: { ...DEFAULT_MEAL_SETTINGS, ...profile.meal_structure_settings },
                        dietPhilosophy: (profile.diet_philosophy as DietPhilosophy) || null,
                        macroProfile: (profile.macro_profile as MacroProfile) || null,
                        householdSize: profile.household_size || 1, // V13
                        subscriptionStatus: profile.subscription_status ?? 'inactive',
                        trialEndsAt: profile.trial_ends_at ?? null,
                        loading: false,
                        error: null,
                    });
                } catch (err) {
                    set({
                        loading: false,
                        error: err instanceof Error ? err : new Error('Failed to load profile'),
                    });
                }
            },

            setVarietyMode: async (newMode, getToken, updateToggles, triggerToast) => {
                // Defensive: Validate input
                if (!VARIETY_MODES.includes(newMode)) {
                    console.error(`Invalid variety mode: ${newMode}`);
                    return;
                }

                const { varietyMode: previousMode, toggles, wineSettings, mealSettings } = get();

                // Optimistic update
                set({ varietyMode: newMode, varietyModeSaving: true });

                try {
                    const token = await getToken();
                    const response = await updateToggles(token, {
                        ...toggles,
                        variety_mode: newMode,
                        wine_pairing_settings: wineSettings,
                        meal_structure_settings: mealSettings,
                        household_size: get().householdSize, // V13
                    });

                    triggerToast?.('Variety mode updated!');
                    return response;
                } catch (error) {
                    // Rollback on failure
                    set({ varietyMode: previousMode });
                    triggerToast?.('Unable to save preference');
                    console.error('Failed to save variety mode:', error);
                } finally {
                    set({ varietyModeSaving: false });
                }
            },

            setToggles: (toggles) => set({ toggles }),
            setMealSettings: (mealSettings) => set({ mealSettings }),
            setWineSettings: (wineSettings) => set({ wineSettings }),

            setDietPhilosophy: async (philosophy, getToken, updateToggles, triggerToast) => {
                const { dietPhilosophy: previous, toggles, wineSettings, mealSettings, varietyMode, macroProfile } = get();
                set({ dietPhilosophy: philosophy });

                try {
                    const token = await getToken();
                    const response = await updateToggles(token, {
                        ...toggles,
                        diet_philosophy: philosophy,
                        macro_profile: macroProfile,
                        variety_mode: varietyMode,
                        wine_pairing_settings: wineSettings,
                        meal_structure_settings: mealSettings,
                        household_size: get().householdSize, // V13
                    });
                    triggerToast?.(`Diet philosophy updated to ${philosophy || 'None'}`);
                    return response;
                } catch (error) {
                    set({ dietPhilosophy: previous });
                    triggerToast?.('Failed to update diet philosophy');
                }
            },

            setMacroProfile: async (profile, getToken, updateToggles, triggerToast) => {
                const { macroProfile: previous, toggles, wineSettings, mealSettings, varietyMode, dietPhilosophy } = get();
                set({ macroProfile: profile });

                try {
                    const token = await getToken();
                    const response = await updateToggles(token, {
                        ...toggles,
                        diet_philosophy: dietPhilosophy,
                        macro_profile: profile,
                        variety_mode: varietyMode,
                        wine_pairing_settings: wineSettings,
                        meal_structure_settings: mealSettings,
                        household_size: get().householdSize, // V13
                    });
                    triggerToast?.(`Macro profile updated to ${profile || 'None'}`);
                    return response;
                } catch (error) {
                    set({ macroProfile: previous });
                    triggerToast?.('Failed to update macro profile');
                }
            },

            setHouseholdSize: async (size, getToken, updateToggles, triggerToast) => {
                const { householdSize: previous, toggles, wineSettings, mealSettings, varietyMode, dietPhilosophy, macroProfile } = get();
                set({ householdSize: size });

                try {
                    const token = await getToken();
                    const response = await updateToggles(token, {
                        ...toggles,
                        diet_philosophy: dietPhilosophy,
                        macro_profile: macroProfile,
                        variety_mode: varietyMode,
                        wine_pairing_settings: wineSettings,
                        meal_structure_settings: mealSettings,
                        household_size: size,
                    });
                    triggerToast?.(`Household size updated to ${size}`);
                    return response;
                } catch (error) {
                    set({ householdSize: previous });
                    triggerToast?.('Failed to update household size');
                }
            },

            reset: () => set(initialState),
        }),
        { name: 'profile-store' }
    )
);

// ============================================================================
// Selectors (for optimized re-renders)
// ============================================================================

export const selectVarietyMode = (state: ProfileStore) => state.varietyMode;
export const selectVarietyModeSaving = (state: ProfileStore) => state.varietyModeSaving;
export const selectUserTier = (state: ProfileStore) => state.userTier;
export const selectToggles = (state: ProfileStore) => state.toggles;
export const selectMealSettings = (state: ProfileStore) => state.mealSettings;
export const selectWineSettings = (state: ProfileStore) => state.wineSettings;
export const selectDietPhilosophy = (state: ProfileStore) => state.dietPhilosophy;
export const selectMacroProfile = (state: ProfileStore) => state.macroProfile;
export const selectProfileLoading = (state: ProfileStore) => state.loading;
